import { useEffect, useRef, useState } from "react";

import { normalizeFetchError } from "../shared/Constants";

/** Minimal shape of the subscription returned by an RTK Query initiate() dispatch. */
interface QuerySubscription<T> {
  unwrap: () => Promise<T[]>;
  unsubscribe: () => void;
}

interface UseParallelPaginatedLoadingConfig<T> {
  /** Active org id; fetches only run when truthy and are namespaced by it. */
  orgId: string;
  /** Total number of items the org has (drives the page count). */
  count: number;
  /** Page size. */
  limit: number;
  /** Optional namespace for the dedupe key, e.g. "processed" for session status. */
  fetchKeyPrefix?: string;
  /** Dispatches an initiate() for one page and returns its subscription. */
  makeRequest: (offset: number, limit: number) => QuerySubscription<T>;
  /** Stable id used to dedupe the merged results. */
  getId: (item: T) => string;
  /** How many items are already stored (short-circuits when everything is present). */
  alreadyLoadedCount: number;
  /** Receives the merged, deduped results on a successful fetch. */
  onLoaded: (items: T[]) => void;
}

interface UseParallelPaginatedLoadingResult {
  isFetching: boolean;
  fetchError: string | null;
  /** True once a fetch has completed successfully and is not in flight (a failed fetch resets this to false to allow retry). */
  loadComplete: boolean;
}

/**
 * Fetches all pages of an org-scoped collection in parallel via RTK Query's
 * initiate() (results are cached by RTK Query), merges and dedupes them, and
 * tracks fetch state. Callers own where the deduped result is stored (local
 * state, Redux, etc.) via onLoaded.
 */
export const useParallelPaginatedLoading = <T>({
  orgId,
  count,
  limit,
  fetchKeyPrefix = "",
  makeRequest,
  getId,
  alreadyLoadedCount,
  onLoaded,
}: UseParallelPaginatedLoadingConfig<T>): UseParallelPaginatedLoadingResult => {
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Tracks org+count to avoid duplicate fetches and re-fetch when org switches
  const hasFetchedForRef = useRef<string>("");

  // Keep callbacks in refs so the effect doesn't re-run when callers pass
  // inline functions (no need for callers to memoize).
  const makeRequestRef = useRef(makeRequest);
  const getIdRef = useRef(getId);
  const onLoadedRef = useRef(onLoaded);
  makeRequestRef.current = makeRequest;
  getIdRef.current = getId;
  onLoadedRef.current = onLoaded;

  const numberOfQueries = Math.ceil(count / limit);
  const hasAllLoaded = count > 0 && alreadyLoadedCount >= count;

  useEffect(() => {
    const fetchKey = fetchKeyPrefix
      ? `${orgId}-${fetchKeyPrefix}-${count}`
      : `${orgId}-${count}`;

    // Nothing to fetch when the org has zero items; mark complete and clear stale data.
    if (orgId && count === 0) {
      setFetchError(null);
      setIsFetching(false);
      if (hasFetchedForRef.current !== fetchKey) {
        hasFetchedForRef.current = fetchKey;
        onLoadedRef.current([]);
      }
      return;
    }

    // The synchronous hasFetchedForRef set below guards against duplicate/
    // concurrent fetches, so isFetching is intentionally not consulted here or
    // tracked as a dependency: doing so would re-run the effect when a failed
    // fetch flips isFetching back to false, retriggering an immediate refetch
    // and looping. A failed fetch instead retries only when a real input
    // (orgId/count) changes.
    const shouldFetch =
      orgId &&
      count > 0 &&
      numberOfQueries > 0 &&
      !hasAllLoaded &&
      hasFetchedForRef.current !== fetchKey;

    if (!shouldFetch) return;

    hasFetchedForRef.current = fetchKey;
    setIsFetching(true);
    setFetchError(null);

    // Guards against a stale fetch resolving after the org changed or the
    // component unmounted, which would otherwise store a previous org's data.
    let cancelled = false;
    const subscriptions: { unsubscribe: () => void }[] = [];

    const fetchAllParallel = async () => {
      const requests = Array.from({ length: numberOfQueries }, (_, i) =>
        makeRequestRef.current(i * limit, limit),
      );
      subscriptions.push(...requests);

      try {
        const results = await Promise.all(
          requests.map((request) => request.unwrap()),
        );
        const merged = results.flat();

        const seenIds = new Set<string>();
        const deduped = merged.filter((item) => {
          const id = getIdRef.current(item);
          if (seenIds.has(id)) return false;
          seenIds.add(id);
          return true;
        });

        if (!cancelled) onLoadedRef.current(deduped);
      } catch {
        if (!cancelled) {
          hasFetchedForRef.current = ""; // Allow retry on next effect run
          setFetchError(normalizeFetchError());
        }
      } finally {
        if (!cancelled) setIsFetching(false);
      }
    };

    fetchAllParallel();

    return () => {
      cancelled = true;
      subscriptions.forEach((s) => {
        s.unsubscribe();
      });
    };
  }, [orgId, count, limit, fetchKeyPrefix, numberOfQueries, hasAllLoaded]);

  // True once a fetch has completed successfully and is not in flight (a failed
  // fetch resets hasFetchedForRef, so this stays false). Used to stop skeleton
  // loaders when deduplication or other factors yield fewer stored items than org count.
  const loadComplete = hasFetchedForRef.current !== "" && !isFetching;

  return { isFetching, fetchError, loadComplete };
};
